import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { ImageDataDb } from '../../../_shared/data/image-data-db.data';
import { ICard } from '../../../_shared/models/card/card.model';
import { ICardGrid } from '../../../_shared/models/card/card-grid.model';
import { ImageData } from '../../../_shared/models/image/image-data.model';
import { CardSize } from '../../../_shared/types/card/card-size.type';
import { CardCatalogueDataSource } from '../../../_shared/types/card-grid-data-source.type';
import { withConfigFallback } from '../../../_shared/utils/with-config-fallback';
import { ContainerComponent } from '../../../container/container.component';
import { CardElementDirective } from '../../card-element.directive';
import { CardCollectionTextComponent } from '../card-collection-text/card-collection-text.component';

@Component({
    selector: 'sirius-card-grid',
    templateUrl: './card-grid.component.html',
    styleUrls: ['./card-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CardCollectionTextComponent, CardElementDirective, ContainerComponent],
    host: {
        '[class.sir-is-small]': 'resolvedCardSize() === "small"',
        '[class.sir-is-medium]': 'resolvedCardSize() === "medium"',
        '[class.sir-is-large]': 'resolvedCardSize() === "large"',
        '[class.is-center]': "resolvedContentAlignment() === 'center'",
    },
})
export class CardGridComponent {
    readonly config = input<ICardGrid | undefined>(undefined);
    readonly dataSource = input<CardCatalogueDataSource>('other');

    // Public inputs keep their natural API name. Consumers bind `[items]`, `[title]`, etc.
    readonly items = input<ICard[]>([]);
    readonly title = input('');
    readonly textHtml = input('');
    readonly eyebrow = input('');
    readonly cardSize = input<CardSize>('small');
    readonly contentAlignment = input<'left' | 'center'>('left');

    // Internal derived signals read by this component's own template only.
    protected readonly resolvedItems = withConfigFallback<ICardGrid, 'items', ICard[]>(this.config, 'items', this.items);
    protected readonly resolvedTitle = withConfigFallback<ICardGrid, 'title', string>(this.config, 'title', this.title);
    protected readonly resolvedTextHtml = withConfigFallback<ICardGrid, 'textHtml', string>(this.config, 'textHtml', this.textHtml);
    protected readonly resolvedEyebrow = withConfigFallback<ICardGrid, 'eyebrow', string>(
        this.config,
        'eyebrow',
        this.eyebrow
    );
    protected readonly resolvedCardSize = withConfigFallback<ICardGrid, 'cardSize', CardSize>(this.config, 'cardSize', this.cardSize);
    protected readonly resolvedContentAlignment = withConfigFallback<
        ICardGrid,
        'contentAlignment',
        'left' | 'center'
    >(this.config, 'contentAlignment', this.contentAlignment);

    readonly selectEvent = output<[ICard, number]>();

    protected readonly imageData = computed<ImageData>(() => {
        switch (this.resolvedCardSize()) {
            case 'small':
                return ImageDataDb.card.small;
            case 'medium':
                return ImageDataDb.card.medium;
            case 'large':
            default:
                return ImageDataDb.card.large;
        }
    });

    onCardSelect(item: ICard, index: number): void {
        this.selectEvent.emit([item, index]);
    }
}
